import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  FaBuilding,
  FaClock,
  FaDownload,
  FaFileAlt,
  FaFolder,
  FaHistory,
  FaLock,
  FaPen,
  FaTrash,
  FaUser,
  FaUserTie,
  FaUsers,
} from "react-icons/fa";
import "./DocumentExplorer.css";

// Building blocks shared by every documents screen, so the Documents page, profile
// documents and organization settings all look and behave the same.

export const formatBytes = (bytes) => {
  if (!bytes) return "0 KB";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const formatDate = (value) => value ? new Intl.DateTimeFormat(undefined, {
  day: "2-digit", month: "short", year: "numeric",
}).format(new Date(value)) : "—";

export const scopeIcon = {
  company: <FaBuilding aria-hidden="true" />,
  restricted: <FaUsers aria-hidden="true" />,
  personal: <FaUser aria-hidden="true" />,
  private: <FaLock aria-hidden="true" />,
  custom: <FaUsers aria-hidden="true" />,
  employee: <FaUserTie aria-hidden="true" />,
};

export const FolderArtwork = ({ folder, variant = folder.visibility }) => {
  const [source, setSource] = useState(null);
  useEffect(() => {
    if (!folder.image_url) return undefined;
    let objectUrl;
    let active = true;
    axios.get(folder.image_url, {
      responseType: "blob",
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    }).then((response) => {
      if (!active) return;
      objectUrl = window.URL.createObjectURL(response.data);
      setSource(objectUrl);
    }).catch(() => setSource(null));
    return () => {
      active = false;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [folder.image_url]);
  if (source) {
    return <div className="document-folder-art document-folder-art--image"><img src={source} alt="" /></div>;
  }
  return <div className={`document-folder-art document-folder-art--${variant}`}><FaFolder /></div>;
};

export const Dialog = ({ title, children, onClose, labelledBy = "document-dialog-title" }) => {
  const { t } = useTranslation();
  return (
    <div className="document-dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="document-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="document-dialog-heading">
          <h2 id={labelledBy}>{title}</h2>
          <button className="document-icon-button" onClick={onClose} aria-label={t("common.actions.close")}>×</button>
        </div>
        {children}
      </section>
    </div>
  );
};

export const VersionDialog = ({ document, versions, onClose, onDownload }) => {
  const { t } = useTranslation();
  return (
    <Dialog title={t("pages.Documents.versionHistory")} onClose={onClose}>
      <p className="document-dialog-lead">{document.title}</p>
      <div className="document-version-list">
        {versions.map((version) => (
          <div key={version.id} className="document-version-row">
            <div><strong>v{version.version_number}</strong><span>{version.original_filename}</span></div>
            <div><span>{formatBytes(version.size_bytes)}</span><span>{formatDate(version.created_at)}</span></div>
            <button className="document-icon-button" aria-label={t("pages.Documents.downloadVersion", { version: version.version_number })} onClick={() => onDownload(version.id)}><FaDownload /></button>
          </div>
        ))}
      </div>
    </Dialog>
  );
};

export const SectionTitle = ({ title, count, className = "" }) => (
  <div className={`document-section-title ${className}`.trim()}><h2>{title}</h2><span>{count}</span></div>
);

export const EmptyState = ({ icon, title, hint, className = "" }) => (
  <div className={`document-empty ${className}`.trim()}>{icon}<strong>{title}</strong><span>{hint}</span></div>
);

/**
 * Folder cards. `renderMeta` fills the footer line and `renderActions` the corner buttons;
 * `variant` picks the artwork colour when a folder has no visibility of its own.
 */
export const FolderGrid = ({ folders, onOpen, renderMeta, renderActions, variant }) => {
  const { t } = useTranslation();
  return (
    <div className="document-folder-grid">
      {folders.map((folder) => {
        const actions = renderActions?.(folder);
        return (
          <article className="document-folder-card" key={folder.id}>
            <button className="document-folder-open" onClick={() => onOpen(folder)}>
              <FolderArtwork folder={folder} variant={variant || folder.visibility} />
              <span className="document-folder-copy"><strong>{folder.name}</strong><small>{folder.description || t("pages.Documents.noDescription")}</small></span>
            </button>
            {renderMeta && <div className="document-folder-meta">{renderMeta(folder)}</div>}
            {actions && <div className="document-folder-actions">{actions}</div>}
          </article>
        );
      })}
    </div>
  );
};

/**
 * Files table. Each optional handler adds its button; `canChange` gates new versions and
 * renames, `canDelete` gates deletion, and `renderNote` adds a line under the file name.
 */
export const FileTable = ({
  documents,
  onDownload,
  onVersions,
  onNewVersion,
  onRename,
  onDelete,
  canChange = () => true,
  canDelete = canChange,
  renderNote,
}) => {
  const { t } = useTranslation();
  return (
    <div className="document-table-wrap"><table className="document-table">
      <thead><tr><th>{t("pages.Documents.name")}</th><th>{t("pages.Documents.version")}</th><th>{t("pages.Documents.size")}</th><th>{t("pages.Documents.modified")}</th><th><span className="visually-hidden">{t("pages.Documents.actions")}</span></th></tr></thead>
      <tbody>{documents.map((document) => {
        const note = renderNote?.(document);
        return (
          <tr key={document.id}>
            <td>
              <span className="document-file-icon"><FaFileAlt /></span>
              <span className="document-file-name"><strong>{document.title}</strong>{note && <small>{note}</small>}</span>
            </td>
            <td>
              {onVersions
                ? <button className="document-version-link" onClick={() => onVersions(document)}><FaClock />v{document.current_version?.version_number || 0}</button>
                : <span>v{document.current_version?.version_number || 0}</span>}
            </td>
            <td>{formatBytes(document.current_version?.size_bytes)}</td>
            <td>{formatDate(document.uploaded_at)}</td>
            <td><div className="document-row-actions">
              <button title={t("pages.Documents.download")} aria-label={t("pages.Documents.download")} onClick={() => onDownload(document)}><FaDownload /></button>
              {onNewVersion && canChange(document) && <button title={t("pages.Documents.newVersion")} aria-label={t("pages.Documents.newVersion")} onClick={() => onNewVersion(document)}><FaHistory /></button>}
              {onRename && canChange(document) && <button title={t("pages.Documents.rename")} aria-label={t("pages.Documents.rename")} onClick={() => onRename(document)}><FaPen /></button>}
              {onDelete && canDelete(document) && <button className="document-action-danger" title={t("common.actions.delete")} aria-label={t("common.actions.delete")} onClick={() => onDelete(document)}><FaTrash /></button>}
            </div></td>
          </tr>
        );
      })}</tbody>
    </table></div>
  );
};
